// URL per resource and per tab (step B1 of the redesign, REDISENO_UI.md 2.2.2
// and 4.3): a pipeline, a function, an agent or an object type has its own
// address, and so does the tab it is open on.
//
//   /connect/pipelines/<id>
//   /model/ontology/<object type id>?tab=properties
//   /build/logic/<id>
//   /build/agents/<id>[/<view>]          (view: a sub-screen, e.g. flujo)
//   /connect/connectors/<id>, /build/evals/<suite id>
//
// The page id does not change (`pipelines` is still `pipelines`, see
// routes.ts RESOURCE_PAGES), so navigateTo callers, permissions and the
// sidebar work as before. This module only reads and writes the part after
// the page path and the ?tab= parameter; pages bind their own selection to it
// with the hooks in useResourceRoute.ts.
import { RESOURCE_PAGES, routeForPage } from './routes';
import type { ResourceKind } from '../design-system/resourceIcons';

/** The resource type each resource page opens. */
export const RESOURCE_KIND_BY_PAGE: Record<string, ResourceKind> = {
  connectors: 'connector',
  pipelines: 'pipeline',
  ontology: 'object_type',
  logic: 'logic_function',
  agents: 'agent',
  evals: 'eval_suite',
  'data-analysis': 'analysis',
  predictions: 'prediction',
  value: 'value_case',
  scenarios: 'scenario',
  discovery: 'discovery',
  'task-mining': 'task_mining',
  mirror: 'mirror',
};

RESOURCE_KIND_BY_PAGE['data-datasets'] = 'dataset';
RESOURCE_KIND_BY_PAGE['process-mining'] = 'process';

export const TAB_PARAM = 'tab';

export interface ResourceLocation {
  page: string;
  /** Null on the page's own path (no resource open). */
  resourceId: string | null;
  /** Segments after the id (/build/agents/<id>/flujo -> ['flujo']). */
  view: string[];
  tab: string | null;
}

export function isResourcePage(page: string): boolean {
  return RESOURCE_PAGES.has(page);
}

function basePath(page: string): string | null {
  const route = routeForPage(page);
  if (!route || !RESOURCE_PAGES.has(route.page)) return null;
  return route.path;
}

/** What a URL says about `page`'s resource, or null when the URL is not on
 *  that page. Ids are decoded; an empty ?tab= counts as none. */
export function parseResourceLocation(page: string, pathname: string, search = ''): ResourceLocation | null {
  const base = basePath(page);
  if (!base) return null;
  const path = pathname.replace(/\/+$/, '') || '/';
  const tab = new URLSearchParams(search).get(TAB_PARAM) || null;
  if (path === base) return { page, resourceId: null, view: [], tab };
  if (!path.startsWith(base + '/')) return null;
  const parts = path.slice(base.length + 1).split('/').filter(Boolean);
  if (!parts.length) return { page, resourceId: null, view: [], tab };
  let decoded: string[];
  try { decoded = parts.map(decodeURIComponent); } catch { return null; }
  return { page, resourceId: decoded[0], view: decoded.slice(1), tab };
}

export interface ResourceHrefOptions {
  tab?: string | null;
  view?: string[];
  /** Query string to carry over (?real=1 in a harness, one-shot flags). The
   *  tab parameter in it is replaced by `tab`. */
  keepSearch?: string;
}

/** Path plus query for a resource of `page` (or the page itself when id is null). */
export function resourceHref(page: string, resourceId: string | null, opts: ResourceHrefOptions = {}): string {
  const base = basePath(page) ?? routeForPage(page)?.path ?? '/';
  let path = base;
  if (resourceId) {
    path += '/' + encodeURIComponent(resourceId);
    for (const v of opts.view ?? []) path += '/' + encodeURIComponent(v);
  }
  const params = new URLSearchParams(opts.keepSearch ?? '');
  params.delete(TAB_PARAM);
  if (resourceId && opts.tab) params.set(TAB_PARAM, opts.tab);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

// ── Location changes ───────────────────────────────────────────────────────
// history.pushState fires no event, so writes from here notify the hooks
// themselves; the browser's back and forward arrive as popstate.

type Listener = () => void;
const listeners = new Set<Listener>();
const hasWindow = typeof window !== 'undefined';

export function subscribeLocation(fn: Listener): () => void {
  listeners.add(fn);
  if (listeners.size === 1 && hasWindow) window.addEventListener('popstate', onPopstate);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && hasWindow) window.removeEventListener('popstate', onPopstate);
  };
}

export function emitLocation() {
  for (const fn of [...listeners]) fn();
}

/** pathname + search, the snapshot the hooks compare. */
export function locationSnapshot(): string {
  return hasWindow ? window.location.pathname + window.location.search : '/';
}

// The address the shell last wrote or accepted, and whether the latest change
// came from the browser's own buttons. A page with unsaved work may refuse a
// change that the browser already made; the address then goes back to
// `settled`, and the binding only asks for changes that came from history,
// since every other exit asks where the user clicked it.
let settled = locationSnapshot();
let fromHistory = false;

function onPopstate() {
  fromHistory = true;
  emitLocation();
}

/** The address bar is where the shell means it to be. */
export function noteLocation(): void {
  settled = locationSnapshot();
  fromHistory = false;
}

/** True while the latest address change came from back or forward. */
export function locationFromHistory(): boolean {
  return fromHistory;
}

/** Put the address bar back on the last noted address, as a new entry. */
export function restoreLocation(page: string): void {
  writeLocation(settled, 'push', page);
}

export type WriteMode = 'push' | 'replace';

/** Move the address bar to `href`. A no-op when it is already there, so a
 *  state that came from the URL is never pushed back onto history. */
export function writeLocation(href: string, mode: WriteMode, page: string): boolean {
  if (!hasWindow) return false;
  if (locationSnapshot() === href) return false;
  try {
    const state = { page, appId: null };
    if (mode === 'push') window.history.pushState(state, '', href + window.location.hash);
    else window.history.replaceState(state, '', href + window.location.hash);
  } catch {
    return false; // sandboxed iframes can refuse history writes
  }
  noteLocation();
  emitLocation();
  return true;
}

/** Open (or close, with null) a resource of `page` in the address bar,
 *  keeping the query string other than ?tab=. */
export function writeResource(page: string, resourceId: string | null, mode: WriteMode, opts: { tab?: string | null; view?: string[] } = {}): boolean {
  if (!hasWindow) return false;
  return writeLocation(resourceHref(page, resourceId, { ...opts, keepSearch: window.location.search }), mode, page);
}

/** Set or clear ?tab= on the current address. */
export function writeTab(page: string, tab: string | null, mode: WriteMode): boolean {
  if (!hasWindow) return false;
  const params = new URLSearchParams(window.location.search);
  if (tab) params.set(TAB_PARAM, tab);
  else params.delete(TAB_PARAM);
  const qs = params.toString();
  return writeLocation(window.location.pathname + (qs ? `?${qs}` : ''), mode, page);
}
